// @ts-nocheck
import { getQuestionSet } from '@repo/api-client';
import { LARGE_SET_QUESTION_COUNT } from '@repo/shared';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { AdminAddSet } from '../../components/editor/AdminAddSet';
import { EditorLoadingSkeleton } from '../../components/editor/EditorLoadingSkeleton';
import { LargeSetPanel } from '../../components/editor/large-set/LargeSetPanel';
import { QuestionEditPage } from '../../components/editor/large-set/QuestionEditPage';
import { MathJaxProvider } from '../../components/rich-text';
import {
  setsKeys,
  useCreateQuestionSet,
  useQuestionSet,
  useQuestionSetSummary,
  useUpdateQuestionSet,
} from '../../queries/sets';
import { hydrateSetDetailIntoEditorState } from '../../utils/hydrateSetDetail';
import { useAdmin } from './-context';

export const Route = createFileRoute('/_admin/add')({
  validateSearch: (search): { setId?: string; questionId?: string; fixture?: string } => ({
    ...(typeof search.setId === 'string' ? { setId: search.setId } : {}),
    ...(typeof search.questionId === 'string' ? { questionId: search.questionId } : {}),
    ...(typeof search.fixture === 'string' ? { fixture: search.fixture } : {}),
  }),
  component: AddRoute,
});

function AddRoute() {
  const { setId, questionId, fixture } = Route.useSearch();
  return (
    <MathJaxProvider>
      <div className="h-full w-full flex flex-col overflow-hidden px-6 pt-8">
        {questionId && setId ? (
          <QuestionEditPage setId={setId} questionId={questionId} />
        ) : (
          <SetEditRoute setId={setId} fixture={fixture} />
        )}
      </div>
    </MathJaxProvider>
  );
}

function SetEditRoute({ setId, fixture }: { setId?: string; fixture?: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    editingSet,
    addQuestion,
    updateQuestion,
    deleteQuestion,
    curricula,
    versions,
    classes,
    subjects,
    chapters,
    topics,
    subtopics,
    isLoading,
  } = useAdmin();

  // ── Backend-set fetch (only when ?setId=<uuid> is present) ──────────────
  const summaryQuery = useQuestionSetSummary(setId);
  const largeRef = useRef<Record<string, boolean>>({});
  if (setId && summaryQuery.data && largeRef.current[setId] === undefined) {
    largeRef.current[setId] = summaryQuery.data.questionCount > LARGE_SET_QUESTION_COUNT;
  }
  const latched = setId ? largeRef.current[setId] : undefined;
  const isLarge = latched === true;
  const setQuery = useQuestionSet(setId, { enabled: latched === false });
  const loadError = summaryQuery.isError ? summaryQuery.error : setQuery.error;

  // Redirect to /sets when the requested setId is missing (404) — surfaces
  // the failure to the curator instead of leaving the wizard blank.
  useEffect(() => {
    if (setId && (summaryQuery.isError || setQuery.isError)) {
      const status = (loadError as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        toast.error('সেট পাওয়া যায়নি');
      } else {
        const message = (loadError as { message?: string })?.message ?? 'সেট লোড করা যায়নি';
        toast.error(message);
      }
      navigate({ to: '/sets' });
    }
  }, [setId, summaryQuery.isError, setQuery.isError, loadError, navigate]);

  // Mutations
  const createMutation = useCreateQuestionSet();
  const updateMutation = useUpdateQuestionSet();

  // Extras carrier (rowVersion + importanceByStandard). The editor reads on
  // save, writes after successful PUT. A ref (not state) because re-render
  // on rowVersion change would invalidate the form's RHF reset cycle.
  const extrasRef = useRef(null);

  const onCreateSet = useCallback(
    async (payload) => {
      const response = await createMutation.mutateAsync(payload);
      // T-QUESTIONS-LAST-FILTER-PREFILL: on success, write the saved set's
      // taxonomy scope (curriculumId/versionId/classId/subjectId as string IDs)
      // to `qm.questions.lastFilter` via `saveLastFilter` from
      // `components/questions/layout/questionsFilterStorage`. This soft pre-fills
      // /questions on next visit into the curator's working scope. Wire once this
      // route file gets its type-cleanup pass (currently the untyped path blocks
      // clean payload access).
      toast.success('সেট তৈরি হয়েছে');
      return response;
    },
    [createMutation],
  );

  const onUpdateSet = useCallback(
    async (id, body) => {
      const response = await updateMutation.mutateAsync({ id, body });
      // Refresh the detail cache so a second save on this page uses the new
      // rowVersion (the editor's extrasRef also stashes it, but the cache
      // matters for navigation away + back).
      queryClient.setQueryData(setsKeys.detail(id), response);
      toast.success('সেট আপডেট হয়েছে');
      return response;
    },
    [updateMutation, queryClient],
  );

  // 409 reload-prompt handler. Refetches the set, re-hydrates the form via
  // the same reverse adapter the route uses on mount, and re-stashes the
  // extras with the new authoritative rowVersion.
  const onReloadAfterConflict = useCallback(async () => {
    if (!setId) return;
    const fresh = await queryClient.fetchQuery({
      queryKey: setsKeys.detail(setId),
      queryFn: () => getQuestionSet(setId),
    });
    const hydrated = hydrateSetDetailIntoEditorState(fresh);
    // The editor's hydration effect watches `backendSetDetail` for identity
    // change — the new query result is a fresh object, so writing it to the
    // cache + the editor remounting on the next render naturally re-runs
    // hydration. Stash the extras now too so the immediate next save uses
    // the new rowVersion.
    extrasRef.current = hydrated.extras;
  }, [setId, queryClient]);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const awaitingSet =
    setId != null && (summaryQuery.isPending || (!isLarge && setQuery.data == null));

  return awaitingSet ? (
    <EditorLoadingSkeleton />
  ) : isLarge && setId ? (
    <LargeSetPanel setId={setId} />
  ) : (
    <AdminAddSet
      key={setId ?? 'new'}
      curricula={curricula}
      versions={versions}
      classes={classes}
      subjects={subjects}
      chapters={chapters}
      topics={topics}
      subtopics={subtopics}
      isLoading={isLoading || (setId != null && setQuery.isLoading)}
      // In-memory mode (no setId): editingSet from legacy AdminContext.
      // Backend mode (setId present): editingSet stays null; pass
      // backendSetDetail so the editor uses the reverse adapter.
      editingSet={setId ? null : editingSet}
      backendSetDetail={setId ? (setQuery.data ?? null) : null}
      onSave={addQuestion}
      onUpdate={updateQuestion}
      onDelete={deleteQuestion}
      onCreateSet={onCreateSet}
      onUpdateSet={onUpdateSet}
      extrasRef={extrasRef}
      onReloadAfterConflict={onReloadAfterConflict}
      isSaving={isSaving}
      isEditSession={setId != null}
      fixtureSlug={setId ? undefined : fixture}
      onCancelEdit={() => navigate({ to: '/questions' })}
    />
  );
}
